import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RefreshCw,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Loader2,
  Clock,
} from 'lucide-react';
import { format } from 'date-fns';
import knowledgeGapsService, { type KGRunResponse } from '@/lib/api/knowledgeGaps';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// ─── Constants ──────────────────────────────────────────────────────────────

const PAGE_SIZE = 15;

const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-500',
  running: 'bg-yellow-500',
  failed: 'bg-red-500',
  pending: 'bg-gray-400',
};

const STATUS_BADGE_VARIANT: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  completed: 'default',
  running: 'secondary',
  failed: 'destructive',
  pending: 'outline',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'MMM d, yyyy h:mm:ss a');
  } catch {
    return iso;
  }
}

// ─── Detail panel ───────────────────────────────────────────────────────────

function RunDetail({ run }: { run: KGRunResponse }) {
  const statusColor = STATUS_COLORS[run.status] ?? 'bg-gray-400';
  const badgeVariant = STATUS_BADGE_VARIANT[run.status] ?? 'outline';

  return (
    <div className="flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b card-border px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold font-mono">
              {run.id.slice(0, 8)}...
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {run.trigger_type} run
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div
              className={cn('h-3 w-3 rounded-full shrink-0', statusColor)}
            />
            <Badge variant={badgeVariant} className="uppercase text-xs">
              {run.status}
            </Badge>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-5 space-y-6">
        {/* Attributes */}
        <section>
          <h3 className="text-sm font-semibold border-b border-border pb-2 mb-3">
            Run Details
          </h3>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <span className="text-muted-foreground">Status</span>
            <span className="capitalize">{run.status}</span>
            <span className="text-muted-foreground">Trigger</span>
            <span>{run.trigger_type}</span>
            <span className="text-muted-foreground">Started</span>
            <span>{formatDate(run.started_at)}</span>
            <span className="text-muted-foreground">Completed</span>
            <span>{formatDate(run.completed_at)}</span>
          </div>
        </section>

        {/* Metrics */}
        <section>
          <h3 className="text-sm font-semibold border-b border-border pb-2 mb-3">
            Results
          </h3>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <span className="text-muted-foreground">Traces Fetched</span>
            <span>{run.traces_fetched}</span>
            <span className="text-muted-foreground">Traces Analyzed</span>
            <span>{run.traces_analyzed}</span>
            <span className="text-muted-foreground">Findings Created</span>
            <span>{run.findings_created}</span>
            <span className="text-muted-foreground">Summaries Generated</span>
            <span>{run.summaries_created}</span>
          </div>
        </section>

        {/* Error */}
        {run.error_message && (
          <section>
            <h3 className="text-sm font-semibold border-b border-border pb-2 mb-3">
              Error
            </h3>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{run.error_message}</AlertDescription>
            </Alert>
          </section>
        )}
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

interface RunHistoryTabProps {
  orgId: string;
}

export function RunHistoryTab({ orgId }: RunHistoryTabProps) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['knowledge-gaps-runs', orgId, page],
    queryFn: async () => {
      const res = await knowledgeGapsService.getRuns(orgId, {
        page,
        limit: PAGE_SIZE,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Failed to load run history');
      }
      return res.data;
    },
    enabled: !!orgId,
    refetchInterval: (query) => {
      const runs = query.state.data?.runs;
      if (runs?.some((r) => r.status === 'pending' || r.status === 'running')) {
        return 5000;
      }
      return false;
    },
  });

  const runs = data?.runs ?? [];
  const totalRuns = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRuns / PAGE_SIZE));

  const selectedRun = runs.find((r) => r.id === selectedRunId) ?? null;

  // Auto-select first run when data loads
  useEffect(() => {
    if (runs.length > 0 && !selectedRunId) {
      setSelectedRunId(runs[0].id);
    }
  }, [runs, selectedRunId]);

  // ── Error state ─────────────────────────────────────────────────────────

  if (isError && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">
          Failed to load run history.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Master-detail layout */}
      {isLoading ? (
        <div className="flex h-[calc(100vh-13rem)] rounded-xl border card-border card-bg overflow-hidden">
          <div className="w-[350px] shrink-0 border-r card-border p-3 space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      ) : runs.length === 0 ? (
        <EmptyState
          icon={Lightbulb}
          title="No pipeline runs yet"
          description="Go to the Trigger Pipeline tab to start your first analysis."
        />
      ) : (
        <div className="flex h-[calc(100vh-13rem)] rounded-xl border card-border card-bg overflow-hidden">
          {/* Left: run list */}
          <div className="w-[350px] border-r card-border flex flex-col card-bg shrink-0">
            {/* Header */}
            <div className="px-4 py-3 min-h-[52px] border-b card-border flex items-center gap-3">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-base font-medium">Runs</h3>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 w-7 p-0"
                disabled={isFetching}
                onClick={() => {
                  queryClient.invalidateQueries({
                    queryKey: ['knowledge-gaps-runs', orgId],
                  });
                }}
              >
                <RefreshCw
                  className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')}
                />
              </Button>
              <span className="card-secondary-bg text-sm px-2 py-0.5 rounded-md">
                {totalRuns}
              </span>
            </div>

            {/* Scrollable list */}
            <div className="flex-1 overflow-auto p-3 space-y-2">
              {runs.map((run: KGRunResponse) => {
                const isSelected = selectedRunId === run.id;
                const statusColor = STATUS_COLORS[run.status] ?? 'bg-gray-400';
                const badgeVariant = STATUS_BADGE_VARIANT[run.status] ?? 'outline';

                return (
                  <div
                    key={run.id}
                    className={cn(
                      'w-full rounded-xl border p-3 text-left transition cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm',
                      isSelected
                        ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20'
                        : 'card-border card-secondary-bg',
                    )}
                    onClick={() => setSelectedRunId(run.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-0.5 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div
                            className={cn('h-2.5 w-2.5 rounded-full shrink-0', statusColor)}
                          />
                          <span className="text-sm font-semibold font-mono truncate">
                            {run.id.slice(0, 8)}...
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(run.started_at)}
                        </div>
                      </div>
                      <Badge
                        variant={badgeVariant}
                        className="shrink-0 text-xs uppercase"
                      >
                        {run.status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t card-border px-4 py-2 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                      setSelectedRunId(null);
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage((p) => Math.min(totalPages, p + 1));
                      setSelectedRunId(null);
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right: detail panel */}
          <div className="flex-1 flex flex-col card-bg overflow-hidden">
            {selectedRun ? (
              <RunDetail run={selectedRun} />
            ) : (
              <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                Select a run to view details.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
